/**
 * Discover foiling-state filter options from unfiltered maneuver table data.
 * State chips / ALL pill must reflect all states present when user filters are cleared.
 */

import { apiEndpoints } from '@config/env';
import { getData } from './global';
import { extractManeuverFoilingStates } from './maneuverFoilingState';

/** Minimal channels — State is always returned via tags in maneuver table SQL. */
export const MANEUVER_STATE_DISCOVERY_CHANNELS = ['Tws_avg'] as const;

function isCancelled(result: { type?: string; error?: string }): boolean {
  return result?.type === 'AbortError' || result?.error === 'Request cancelled';
}

async function statesFromManeuverTableUrl(url: string, signal?: AbortSignal): Promise<string[]> {
  const result = await getData(url, signal);
  if (isCancelled(result)) return [];
  if (result?.success && Array.isArray(result.data)) {
    return extractManeuverFoilingStates(result.data);
  }
  return [];
}

export interface DatasetManeuverStateOptionsParams {
  className: string;
  projectId: number | string;
  datasetId: number | string;
  eventType: string;
  signal?: AbortSignal;
}

export async function fetchDatasetManeuverStateOptions(
  params: DatasetManeuverStateOptionsParams
): Promise<string[]> {
  const url =
    `${apiEndpoints.app.data}/maneuvers-table-data` +
    `?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(String(params.projectId))}` +
    `&dataset_id=${encodeURIComponent(String(params.datasetId))}` +
    `&event_type=${encodeURIComponent(params.eventType)}` +
    `&channels=${encodeURIComponent(JSON.stringify(MANEUVER_STATE_DISCOVERY_CHANNELS))}`;
  return statesFromManeuverTableUrl(url, params.signal);
}

export interface FleetDayManeuverStateOptionsParams {
  className: string;
  projectId: number | string;
  date: string;
  eventType: string;
  sourceNames: string[];
  liveOnly?: boolean;
  signal?: AbortSignal;
}

/**
 * One request for the whole fleet — /maneuvers accepts source_names as an array, so this does not
 * need to fan out per source (the history variant below has always passed the array directly).
 */
export async function fetchFleetDayManeuverStateOptions(
  params: FleetDayManeuverStateOptionsParams
): Promise<string[]> {
  if (params.sourceNames.length === 0) return [];
  const liveSuffix = params.liveOnly ? '&live_only=true' : '';
  const url =
    `${apiEndpoints.app.data}/maneuvers` +
    `?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(String(params.projectId))}` +
    `&date=${encodeURIComponent(params.date)}` +
    `&event_type=${encodeURIComponent(params.eventType)}` +
    `&channels=${encodeURIComponent(JSON.stringify(MANEUVER_STATE_DISCOVERY_CHANNELS))}` +
    liveSuffix +
    `&source_names=${encodeURIComponent(JSON.stringify(params.sourceNames))}`;
  return statesFromManeuverTableUrl(url, params.signal);
}

export interface HistoryManeuverStateOptionsParams {
  className: string;
  projectId: number | string;
  sourceNames: string[];
  startDate: string;
  endDate: string;
  eventType: string;
  count?: number;
  signal?: AbortSignal;
}

export async function fetchHistoryManeuverStateOptions(
  params: HistoryManeuverStateOptionsParams
): Promise<string[]> {
  let url =
    `${apiEndpoints.app.data}/maneuvers-history` +
    `?class_name=${encodeURIComponent(params.className)}` +
    `&project_id=${encodeURIComponent(String(params.projectId))}` +
    `&source_names=${encodeURIComponent(JSON.stringify(params.sourceNames))}` +
    `&start_date=${encodeURIComponent(params.startDate)}` +
    `&end_date=${encodeURIComponent(params.endDate)}` +
    `&event_type=${encodeURIComponent(params.eventType)}` +
    `&channels=${encodeURIComponent(JSON.stringify(MANEUVER_STATE_DISCOVERY_CHANNELS))}`;
  if (params.count != null && params.count > 0) {
    url += `&count=${encodeURIComponent(String(params.count))}`;
  }
  return statesFromManeuverTableUrl(url, params.signal);
}
